import React, { useState, useEffect } from 'react';
import { Bell, X, Check, User, Mail, Calendar } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import API_URL from '../config/API_URL';

// NotificationBell Component - Add this to your Header
export const NotificationBell = ({ userId, familyId }) => {
  const { showToast, ToastContainer } = useToast();
  
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch pending requests
  const fetchPendingRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/family/${familyId}/requests`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  // Poll for new requests every 30 seconds
  useEffect(() => {
    if (familyId) {
      fetchPendingRequests();
      const interval = setInterval(fetchPendingRequests, 60000);
      return () => clearInterval(interval);
    }
  }, [familyId]);

  // Handle approve/reject
  const handleReviewRequest = async (requestId, action) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/family/${familyId}/requests/${requestId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ action })
        }
      );

      if (response.ok) {
        // Remove the request from the list
        setPendingRequests(prev => prev.filter(req => req.id !== requestId));
        
        // Show success toast instead of alert
        showToast(
          `Request ${action}d successfully!`,
          'success',
          3000
        );
      } else {
        const error = await response.json();
        // Show error toast instead of alert
        showToast(
          error.error || 'Failed to process request',
          'error',
          4000
        );
      }
    } catch (error) {
      console.error('Error reviewing request:', error);
      // Show error toast instead of alert
      showToast(
        'Failed to process request',
        'error',
        4000
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      {/* Toast Container - renders all toasts */}
      <ToastContainer />
      
      {/* Notification Bell Button */}
      <button
        onClick={() => setShowModal(true)}
        style={{
          position: 'relative',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          padding: '0.5rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#f9fafb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'white';
        }}
      >
        <Bell size={20} color="#374151" />
        
        {/* Notification Badge */}
        {pendingRequests.length > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: '#dc2626',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              borderRadius: '9999px',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid white'
            }}
          >
            {pendingRequests.length}
          </span>
        )}
      </button>

      {/* Rest of your modal code remains the same... */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '1rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
                  Join Requests
                </h2>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  {pendingRequests.length} pending {pendingRequests.length === 1 ? 'request' : 'requests'}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <X size={24} color="#6b7280" />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem' }}>
              {pendingRequests.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                  <Bell size={48} color="#d1d5db" style={{ margin: '0 auto 1rem' }} />
                  <p style={{ color: '#6b7280', fontSize: '1rem' }}>
                    No pending join requests
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {pendingRequests.map((request) => (
                    <div
                      key={request.id}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.75rem',
                        padding: '1.25rem',
                        background: '#f9fafb'
                      }}
                    >
                      {/* User Info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                        <div
                          style={{
                            background: '#dbeafe',
                            borderRadius: '50%',
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <User size={24} color="#2563eb" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontWeight: '600', color: '#1f2937', fontSize: '1.125rem' }}>
                            {request.requester?.first_name} {request.requester?.last_name}
                          </h3>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.25rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Mail size={14} color="#6b7280" />
                              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                {request.requester?.email}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Calendar size={14} color="#6b7280" />
                              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Requested {formatDate(request.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                          onClick={() => handleReviewRequest(request.id, 'reject')}
                          disabled={loading}
                          style={{
                            flex: 1,
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            padding: '0.75rem 1rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            opacity: loading ? 0.6 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!loading) e.currentTarget.style.background = '#b91c1c';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#dc2626';
                          }}
                        >
                          <X size={16} />
                          Reject
                        </button>
                        <button
                          onClick={() => handleReviewRequest(request.id, 'approve')}
                          disabled={loading}
                          style={{
                            flex: 1,
                            background: '#059669',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            padding: '0.75rem 1rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            opacity: loading ? 0.6 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!loading) e.currentTarget.style.background = '#047857';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#059669';
                          }}
                        >
                          <Check size={16} />
                          Approve
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};